import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_ANALYSIS_INTENT_TOP_BREAKDOWN,
  ADMIN_ANALYSIS_MESSAGES,
  ADMIN_ANALYSIS_NEGATIVE_FEEDBACK,
  ADMIN_ANALYSIS_POSITIVE_FEEDBACK,
  ADMIN_ANALYSIS_SESSION,
} from "@/lib/graphql/queries/analysis";
import type {
  AnalysisFilters,
  AnalysisMessageList,
  AnalysisSessionDetail,
  IntentTopBreakdownRow,
  SentimentDistribution,
} from "@/types/analysis";

export const analysisKeys = {
  all: ["analysis"] as const,
  topBreakdown: (filters: AnalysisFilters) =>
    [...analysisKeys.all, "topBreakdown", filters] as const,
  negative: (filters: AnalysisFilters) =>
    [...analysisKeys.all, "negative", filters] as const,
  positive: (filters: AnalysisFilters) =>
    [...analysisKeys.all, "positive", filters] as const,
  messages: (filters: AnalysisFilters, limit: number) =>
    [...analysisKeys.all, "messages", filters, limit] as const,
  session: (id: string) => [...analysisKeys.all, "session", id] as const,
};

const STALE_TIME = 60_000;
export const MESSAGES_PAGE_SIZE = 50;

export const intentTopBreakdownQueryOptions = (filters: AnalysisFilters) =>
  queryOptions({
    queryKey: analysisKeys.topBreakdown(filters),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminAnalysisIntentTopBreakdown: IntentTopBreakdownRow[];
      }>(ADMIN_ANALYSIS_INTENT_TOP_BREAKDOWN, { filters });
      return data.adminAnalysisIntentTopBreakdown;
    },
    staleTime: STALE_TIME,
  });

export const negativeFeedbackQueryOptions = (filters: AnalysisFilters) =>
  queryOptions({
    queryKey: analysisKeys.negative(filters),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminAnalysisNegativeFeedback: SentimentDistribution;
      }>(ADMIN_ANALYSIS_NEGATIVE_FEEDBACK, { filters });
      return data.adminAnalysisNegativeFeedback;
    },
    staleTime: STALE_TIME,
  });

export const positiveFeedbackQueryOptions = (filters: AnalysisFilters) =>
  queryOptions({
    queryKey: analysisKeys.positive(filters),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminAnalysisPositiveFeedback: SentimentDistribution;
      }>(ADMIN_ANALYSIS_POSITIVE_FEEDBACK, { filters });
      return data.adminAnalysisPositiveFeedback;
    },
    staleTime: STALE_TIME,
  });

export const analysisMessagesInfiniteQueryOptions = (
  filters: AnalysisFilters,
  limit: number = MESSAGES_PAGE_SIZE,
) =>
  infiniteQueryOptions({
    queryKey: analysisKeys.messages(filters, limit),
    queryFn: async ({ pageParam }) => {
      const data = await authenticatedRequest<{
        adminAnalysisMessages: AnalysisMessageList;
      }>(ADMIN_ANALYSIS_MESSAGES, {
        input: { filters, offset: pageParam, limit },
      });
      return { ...data.adminAnalysisMessages, offset: pageParam };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.rows.length;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    staleTime: STALE_TIME,
  });

export const analysisSessionQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: analysisKeys.session(id ?? ""),
    queryFn: async () => {
      if (!id) return null;
      const data = await authenticatedRequest<{
        adminAnalysisSession: AnalysisSessionDetail | null;
      }>(ADMIN_ANALYSIS_SESSION, { id });
      return data.adminAnalysisSession;
    },
    enabled: !!id,
    staleTime: STALE_TIME,
  });
